import {BarChartOutlined, DownOutlined, FileDoneOutlined, InfoCircleOutlined} from "@ant-design/icons";
import {
  Breadcrumb,
  Button,
  Divider,
  Empty,
  Layout,
  Result,
  Skeleton,
  Table,
  notification,
  theme, Dropdown, Space, DatePicker, Form
} from "antd";
import dayjs from "dayjs";
import {useEffect, useState} from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {apiReceivablesDownload, apiReceivablesList, apiSmartFactoryDownload} from "../../../../api/receivables";
import { useIsMobileHook } from "../../../../utils/WindowHooks";
import { formatDate, formatPrice, formatExpectedOnDate } from "../../../../utils/format";
import AccountsSearchSelect from "../../../common/AccountsSearchSelect/AccountsSearchSelect";
import "./ReceivableListing.scss";

const { RangePicker } = DatePicker;
const { Content } = Layout;

const ReceivableListing = () => {
  const [searchParams] = useSearchParams();
  const [dataSales, setDataSale] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const isMobile = useIsMobileHook(1600);
  const [filteredData, setFilteredData] = useState(dataSales?.results);
  const [pagination, setPagination] = useState({
    current: 1,
    total: 0,
  });
  const navigate = useNavigate();
  const [activeError, setActiveError] = useState(false);
  const [errorStatus, setErrorStatus] = useState("");
  const [smartFactoryLoading, setSmartFactoryLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [dateRange, setDateRange] = useState(() => {
    const from = searchParams.get("from");
    const to = searchParams.get("to");
    if (from && to) {
      return [dayjs(from), dayjs(to)];
    }
    return [dayjs(), dayjs().add(1, "day")];
  });
  const [sellerFilter, setSellerFilter] = useState();

  const handleRowClick = (record) => {
    const id = record.id;
    const newExpandedRowKeys = [...expandedRowKeys];
    const index = newExpandedRowKeys.indexOf(id);
    if (index > -1) {
      newExpandedRowKeys.splice(index, 1);
    } else {
      newExpandedRowKeys.push(id);
    }
    setExpandedRowKeys(newExpandedRowKeys);
  };

  useEffect(() => {
    setPagination({
      current: 1,
      total: 0,
    })
    fetchData({
      acquirer_account: sellerFilter ? sellerFilter.id : undefined,
      expected_on_gte: dateRange[0]?.format("YYYY-MM-DD") || undefined,
      expected_on_lte: dateRange[1]?.format("YYYY-MM-DD") || undefined,
    });
  }, [sellerFilter, dateRange]);

  const fetchData = async (params) => {
    setIsFetching(true);
    try {
      const filteredParams = Object.fromEntries(
        Object.entries(params).filter(([_, value]) => value !== undefined && value !== null && value !== '')
      );
      const json = await apiReceivablesList(filteredParams);
      setDataSale(json);
      setFilteredData(json.results);
      setPagination((prevPagination) => ({
        ...prevPagination,
        total: json.count,
        current: params.page,
      }));
    } catch (error) {
      setActiveError(true);
      setErrorStatus(error.status);
      notification.error({
        message: "Erro",
        description: "Ocorreu um erro ao buscar os recebiveis.",
        placement: "topRight",
      });
    } finally {
      setIsFetching(false);
    }
  };

  const onChangePage = (pagination) => {
    fetchData({
      page: pagination.current,
      acquirer_account: sellerFilter ? sellerFilter.id : undefined,
      expected_on_gte: dateRange[0]?.format("YYYY-MM-DD") || undefined,
      expected_on_lte: dateRange[1]?.format("YYYY-MM-DD") || undefined,
    });
  };

  const statusMap = {
    paid: <span className="badge badge-success">Confirmada</span>,
    deleted: <span className="badge badge-error">Deletado</span>,
    scheduled: <span className="badge yellow">Agendado</span>,
    pending: <span className="badge orange">Pendente</span>,
    created: <span className="badge orange">Pendente</span>,
    refunded: <span className="badge badge-error">Estornado</span>,
  };

  const renderStatus = (status) => {
    return statusMap[status] || <span className="badge black">Indefinido</span>;
  };

  const items = [
    {
      label: 'CSV',
      key: 'csv',
      icon: <FileDoneOutlined />,
    },
    {
      label: 'EXCEL',
      key: 'excel',
      icon: <FileDoneOutlined />,
    },
  ];
  const handleDownload = async (format, type) => {
    const filters = {
      acquirer_account: sellerFilter ? sellerFilter.id : undefined,
      expected_on_gte: dateRange[0]?.format("YYYY-MM-DD") || undefined,
      expected_on_lte: dateRange[1]?.format("YYYY-MM-DD") || undefined,
    };

    const filteredParams = Object.fromEntries(
        Object.entries(filters).filter(([_, value]) => value !== undefined)
    );

    try {
      let blob;
      if (type === "Relatorio-SmartFactor") {
        setSmartFactoryLoading(true);
        blob = await apiSmartFactoryDownload(format, filteredParams);
      } else {
        setDownloadLoading(true);
        blob = await apiReceivablesDownload(format, filteredParams);
      }
      const date = new Date().toISOString().slice(0, 10);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}-${date}.${format === 'csv' ? 'csv' : 'xlsx'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Failed to download file:', error);
    } finally {
      if (type === "Relatorio-SmartFactor") {
        setSmartFactoryLoading(false);
      } else {
        setDownloadLoading(false);
      }
    }
  };

  const handleSmartFactoryConfirm = async (selectedFormat) => {
    const filters = {
      acquirer_account: sellerFilter ? sellerFilter.id : undefined,
      expected_on_gte: dateRange[0]?.format("YYYY-MM-DD") || undefined,
      expected_on_lte: dateRange[1]?.format("YYYY-MM-DD") || undefined,
    };

    const filteredParams = Object.fromEntries(
        Object.entries(filters).filter(([_, value]) => value !== undefined)
    );

    try {
      setSmartFactoryLoading(true);
      const blob = await apiSmartFactoryDownload(selectedFormat, filteredParams);
      const date = new Date().toISOString().slice(0, 10);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Relatorio-SmartFactor-${date}.${selectedFormat === 'csv' ? 'csv' : 'xlsx'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      // Show success notification
      notification.success({
        message: "Sucesso",
        description: "Download do relatório iniciado!",
        placement: "topRight",
      });
    } catch (error) {
      console.error('Failed to download file:', error);
      notification.error({
        message: "Erro",
        description: "Ocorreu um erro ao gerar o relatório.",
        placement: "topRight",
      });
    } finally {
      setSmartFactoryLoading(false);
    }
  };
  const smartReportMenuProps = {
    items,
    onClick: (e) => {
      handleSmartFactoryConfirm(e.key);
    },
  };

  const reportMenuProps = {
    items,
    onClick: (e) => {
      handleDownload(e.key, "Recebiveis");
    },
  };

  const columns = [
    {
      title: "Data Prevista",
      dataIndex: "expected_on",
      key: "expected_on",
      onCell: () => ({
        style: { cursor: "pointer" },
      }),
      render: (confirmed) => (
        <p
          style={{ textTransform: "capitalize" }}
        >
          {formatDate(confirmed)}
        </p>
      ),
    },
    {
      title: "Transação",
      dataIndex: "transaction",
      key: "transaction",
      onCell: () => ({
        style: { cursor: "pointer" },
      }),
      render: (confirme) => (
        <Link to={`/transacao/${confirme}`}>
          {confirme}
        </Link>
      ),
      responsive: ["sm"],
    },
    {
      title: "Originador",
      dataIndex: ["receiver_account", "display_name"],
      key: "receiver_account__display_name",
      responsive: ["xxl"],
    },
    {
      title: "Conta Base",
      dataIndex: ["acquirer_account", "display_name"],
      key: "acquirer_account__display_name",
      responsive: ["xxl"],
    },
    {
      title: "Tipo",
      dataIndex: "has_split_linked",
      key: "has_split_linked",
      responsive: ["sm"],
      render: (has_split) => (
        <p
          style={{ textTransform: "capitalize" }}
        >
          {has_split ? "Comissão" : "Venda"}
        </p>
      ),
    },
    {
      title: "Data da Criação",
      dataIndex: "created_at",
      key: "created_at",
      responsive: ["sm"],
      render: (confirmed) => (
        <p
          style={{ textTransform: "capitalize" }}
        >
          {formatDate(confirmed)}
        </p>
      ),
    },
    {
      title: "Valor",
      dataIndex: "amount",
      key: "amount",
      render: (confirmed) => (
        <p
          style={{ textTransform: "capitalize" }}
        >
          {formatPrice(confirmed)}
        </p>
      ),
      responsive: ["lg"],
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: renderStatus,
    },
    {
      title: "",
      dataIndex: "",
      render: (confirme) => (
        <Link to={`/recebivel/${confirme.id}`}>
          <InfoCircleOutlined style={{ cursor: "pointer", height: "100%" }} />
        </Link>
      ),
      key: "",
      responsive: ["lg"],
    },
  ];

  const expandedRowRender = (record) => (
    <div className="expandid">
      <span>
       Data Prevista:{" "}
        <p>{formatExpectedOnDate(record.expected_on)}</p>
      </span>
      <span>
        Originador:{" "}
        <p>{record.receiver_account ? record.receiver_account.display_name : ""}</p>
      </span>
      <span>
        Conta Base:{" "}
        <p>{record.acquirer_account ? record.acquirer_account.display_name : ""}</p>
      </span>
      <span>
        Tipo:{" "}
        <p>{record.has_split_linked ? "Comissão" : "Venda"}</p>
      </span>
      <span>
        Valor bruto:{" "}
        <p>{formatPrice(record.amount)}</p>
      </span>
      <span>
        Status:{" "}
        {renderStatus(record.status)}
      </span>
      <span style={{ marginTop: "10px" }}>
        Informação:{" "}
        <Link to={`/recebivel/${record.id}`}>
          <InfoCircleOutlined style={{ cursor: "pointer", height: "100%" }} />
        </Link>
      </span>
    </div>
  );
  const locale = {
    emptyText: <Empty description="Sem dados" />,
  };

  const renderError = (status) => {
    if (activeError) {
      return (
        <Result
          status={status === "500" ? "500" : "404"}
          title={status}
          subTitle={
            status === "500"
              ? "Ocorreu um erro no servidor, tente novamente mais tarde."
              : "Ocorreu um erro, tente novamente mais tarde."
          }
          extra={
            <Button onClick={() => navigate("/")} type="primary">
              Dashboard
            </Button>
          }
        />
      );
    }
  };

  return (
    <div className="ReceivableListing">
      <Layout
        style={{
          padding: "1.25rem",
        }}
      >
        <Breadcrumb
          style={{
            margin: "16px 0",
          }}
        >
          <div style={{ display: "flex", width: "100%", justifyContent: "space-between", alignItems: "center" }}>
            <h2 className="title-page">Agenda de recebimento</h2>
            <Button
              icon={<BarChartOutlined />}
              onClick={() => navigate("/listagem-recebiveis")}
            >
              Modo gráfico
            </Button>
          </div>
          <Divider />
          <Breadcrumb.Item>Minhas vendas</Breadcrumb.Item>
          <Breadcrumb.Item>Agenda de recebimento</Breadcrumb.Item>
        </Breadcrumb>
        <Content
          style={{
            padding: 24,
            margin: 0,
            minHeight: 280,
            background: colorBgContainer,
            borderRadius: borderRadiusLG,
          }}
        >
          <h3 className="subtitle-page">Listagem de recebíveis</h3>
          <Divider />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, flexWrap: 'wrap', gap: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <AccountsSearchSelect
                style={{ width: '200px'}}
                placeholder="Filtrar por conta"
                value={sellerFilter}
                onChange={(value) => setSellerFilter(value)}
                allowClear={true}
              />
            </div>            <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
              <Form.Item label="Período das recebíveis" style={{ margin: 0 }}>
                <RangePicker
                  onChange={(dates) => setDateRange(dates)}
                  value={dateRange}
                  style={{ width: 300 }}
                  placeholder={['Data inicial', 'Data final']}
                  format={{ format: 'DD/MM/YYYY', type: 'mask' }}
                />
              </Form.Item>
            </div>
          </div>
          {isFetching ? (
            <Skeleton active />
          ) : activeError ? (
            renderError(errorStatus)
          ) : (
            <Table
              columns={columns}
              locale={locale}
              expandable={{
                expandedRowRender: isMobile ? expandedRowRender : undefined,
                expandedRowKeys: expandedRowKeys,
                onExpand: (expanded, record) => handleRowClick(record),
                responsive: ["xl"],
              }}
              dataSource={
                filteredData &&
                filteredData.map((item) => ({ ...item, key: item.id }))
              }
              pagination={{
                current: pagination.current,
                total: pagination.total,
                showSizeChanger: false,
              }}
              bordered
              onChange={onChangePage}            />
          )}
          <div className="download-section">
            <Dropdown menu={smartReportMenuProps} disabled={smartFactoryLoading}>
              <Button loading={smartFactoryLoading} disabled={smartFactoryLoading}>
                <Space>
                  Baixar Relatório SmartFactor
                  <DownOutlined />
                </Space>
              </Button>
            </Dropdown>
            <Dropdown menu={reportMenuProps} disabled={downloadLoading}>
              <Button loading={downloadLoading} disabled={downloadLoading}>
                <Space>
                  Baixar Relatório
                  <DownOutlined />
                </Space>
              </Button>
            </Dropdown>
          </div>
        </Content>
      </Layout>
    </div >
  );
};

export default ReceivableListing;